import { Container, Sprite, type Texture } from 'pixi.js';
import { Pool } from '@/core/pool';
import { COLORS } from '@/config/colors';
import { ECONOMY } from '@/config/economy';
import { effectsProfile } from '@/config/fx';
import { PROJECTILE_LOOK as LOOK } from '@/config/projectile-look';
import { getFrames, getTexture, makeSprite } from '@/assets/resolver';
import type { Rng } from '@/core/rng';

export type GemKind = 'yellow' | 'red';

interface Gem {
  x: number;
  y: number;
  vx: number;
  vy: number;
  value: number;
  kind: GemKind;
  animT: number;
  /** Seconds until evaporation; 0 = immortal (red gems) */
  ttl: number;
  /** Inside the magnet radius this step (trail on, bob off) */
  pulled: boolean;
  /** Cut tier by value: 0 single, 1 cluster, 2 hoard (docs/04) */
  cut: number;
  sprite: Sprite;
  /** Magnet trail streak (docs/04 loot language) */
  trail: Sprite;
}

export interface GemSpawnOpts {
  small?: boolean;
  scatterMul?: number;
  /** Juicy bursts (boss payout) keep at least this many pieces */
  minPieces?: number;
}

/** Texture per cut tier (single → cluster → hoard), per kind */
const TEX_KEYS: Record<GemKind, readonly string[]> = {
  yellow: ['pickup/gem-yellow', 'pickup/gem-yellow-cluster', 'pickup/gem-yellow-hoard'],
  red: ['pickup/gem-red', 'pickup/gem-red-cluster', 'pickup/gem-red-hoard'],
};
const TRAIL_TINT: Record<GemKind, number> = { yellow: COLORS.gemYellow, red: COLORS.gemRed };

/**
 * Gem drops: scatter + friction + magnet + collection. Pooled.
 * Yellow = shop currency (mid-fight trickle), red = boss-kill reward
 * used for tier/epic "recipes" (docs/03).
 */
export class GemSystem {
  readonly view = new Container();
  private readonly pool: Pool<Gem>;
  private live: Gem[] = [];
  /** Sparkle frames per kind and cut — resolved once, no per-frame lookups */
  private readonly frames: Record<GemKind, Texture[][]> = {
    yellow: TEX_KEYS.yellow.map((k) => getFrames(k)),
    red: TEX_KEYS.red.map((k) => getFrames(k)),
  };

  constructor() {
    this.pool = new Pool(() => {
      const trail = makeSprite('pickup/gem-trail');
      trail.visible = false;
      this.view.addChild(trail); // under its own gem
      const sprite = new Sprite(getTexture(TEX_KEYS.yellow[0]!));
      sprite.anchor.set(0.5);
      sprite.visible = false;
      this.view.addChild(sprite);
      return {
        x: 0, y: 0, vx: 0, vy: 0, value: 1,
        kind: 'yellow' as GemKind, animT: 0, ttl: 0, pulled: false, cut: 0, sprite, trail,
      };
    }, 64);
  }

  /**
   * Legacy burst API (count × value) — consolidated: the same total value
   * lands as fewer, bigger pieces (docs/03 wave 4 loot audit).
   */
  spawnBurst(
    x: number,
    y: number,
    count: number,
    valuePerGem: number,
    rng: Rng,
    kind: GemKind = 'yellow',
    opts: GemSpawnOpts = {},
  ): void {
    this.spawnValue(x, y, count * valuePerGem, rng, kind, opts);
  }

  /**
   * Scatter `total` value as ceil(total / pieceValue) pieces (at least
   * `minPieces`, never more pieces than value). The value splits evenly —
   * nothing is rounded away. Past `maxLive` a piece merges into the nearest
   * live gem of its kind instead of adding clutter.
   */
  spawnValue(
    x: number, y: number, total: number, rng: Rng, kind: GemKind = 'yellow',
    opts: GemSpawnOpts = {},
  ): void {
    const value = Math.floor(total);
    if (value <= 0) return;
    const per = ECONOMY.gems.pieceValue[kind];
    const pieces = Math.min(value, Math.max(opts.minPieces ?? 1, Math.ceil(value / per)));
    const base = Math.floor(value / pieces);
    let extra = value - base * pieces;
    for (let i = 0; i < pieces; i++) {
      const v = base + (extra > 0 ? 1 : 0);
      if (extra > 0) extra--;
      this.spawnPiece(x, y, v, rng, kind, opts);
    }
  }

  private spawnPiece(
    x: number, y: number, value: number, rng: Rng, kind: GemKind, opts: GemSpawnOpts,
  ): void {
    if (this.countOf(kind) >= ECONOMY.gems.maxLive) {
      const into = this.nearest(x, y, kind);
      if (into) {
        into.value += value;
        // A merged gem is fresh loot again: full timer, bigger sprite
        if (into.ttl > 0) into.ttl = ECONOMY.gems.ttl;
        this.applyScale(into, false);
        return;
      }
    }
    const [minS, maxS] = ECONOMY.gems.scatterSpeed;
    const scatter = opts.scatterMul ?? 1;
    const g = this.pool.acquire();
    const a = rng.range(0, Math.PI * 2);
    const s = rng.range(minS, maxS) * scatter;
    g.x = x;
    g.y = y;
    g.vx = Math.cos(a) * s;
    g.vy = Math.sin(a) * s;
    g.value = value;
    g.kind = kind;
    g.animT = rng.range(0, 1);
    // Yellow gems evaporate (blinking near the end); red ones are forever
    g.ttl = kind === 'yellow' ? ECONOMY.gems.ttl : 0;
    g.sprite.visible = true;
    g.sprite.alpha = 1;
    this.applyScale(g, opts.small === true);
    this.live.push(g);
  }

  /**
   * Bigger value reads bigger AND gets its own cut (cluster / hoard);
   * hit-drip pieces follow the effects setting.
   */
  private applyScale(g: Gem, small: boolean): void {
    let scale = 1;
    for (const tier of ECONOMY.gems.valueScale) if (g.value >= tier.min) scale = tier[g.kind];
    g.cut = 0;
    for (const min of LOOK.gemCuts[g.kind]) if (g.value >= min) g.cut++;
    g.sprite.scale.set(small && g.kind === 'yellow' ? scale * effectsProfile().dripScale : scale);
  }

  private countOf(kind: GemKind): number {
    let n = 0;
    for (const g of this.live) if (g.kind === kind) n++;
    return n;
  }

  private nearest(x: number, y: number, kind: GemKind): Gem | null {
    let best: Gem | null = null;
    let bestD = Infinity;
    for (const g of this.live) {
      if (g.kind !== kind) continue;
      const d = (g.x - x) * (g.x - x) + (g.y - y) * (g.y - y);
      if (d < bestD) {
        bestD = d;
        best = g;
      }
    }
    return best;
  }

  /**
   * @param magnetRadius attraction radius, grows with stat cards
   * @returns gem value collected this frame, per kind
   */
  update(
    dt: number,
    px: number,
    py: number,
    magnetRadius: number,
  ): { yellow: number; red: number } {
    const decay = Math.exp(-ECONOMY.gems.friction * dt);
    const collected = { yellow: 0, red: 0 };

    for (let i = this.live.length - 1; i >= 0; i--) {
      const g = this.live[i]!;
      g.animT += dt;
      if (g.ttl > 0) {
        g.ttl -= dt;
        if (g.ttl <= 0) {
          this.kill(i);
          continue;
        }
      }

      const dx = px - g.x;
      const dy = py - g.y;
      const dist = Math.hypot(dx, dy);

      if (dist < ECONOMY.gems.collectRadius) {
        collected[g.kind] += g.value;
        this.kill(i);
        continue;
      }

      g.pulled = dist < magnetRadius;
      if (g.pulled) {
        // Velocity is steered straight at the player (kills tangential drift,
        // so no orbiting): slow drift when far, hard snap when close.
        const closeness = 1 - dist / magnetRadius;
        const speed = ECONOMY.gems.pullMin + (ECONOMY.gems.pullMax - ECONOMY.gems.pullMin) * closeness * closeness;
        const blend = Math.min(1, 12 * dt);
        g.vx += ((dx / dist) * speed - g.vx) * blend;
        g.vy += ((dy / dist) * speed - g.vy) * blend;
      } else {
        g.vx *= decay;
        g.vy *= decay;
      }

      g.x += g.vx * dt;
      g.y += g.vy * dt;
    }
    return collected;
  }

  private kill(index: number): void {
    const g = this.live[index]!;
    g.sprite.visible = false;
    g.trail.visible = false;
    this.live[index] = this.live[this.live.length - 1]!;
    this.live.pop();
    this.pool.release(g);
  }

  render(): void {
    const blink = ECONOMY.gems.blinkTime;
    for (const g of this.live) {
      // Loot read (docs/04): a gentle bob at rest, a magnet trail when pulled
      const bob = g.pulled ? 0 : Math.sin(g.animT * LOOK.gemBobSpeed) * LOOK.gemBob;
      g.sprite.position.set(g.x, g.y + bob);
      this.renderTrail(g);
      const frames = this.frames[g.kind][g.cut]!;
      g.sprite.texture = frames[Math.floor(g.animT * LOOK.sparkleFps) % frames.length]!;
      // Evaporation warning: blink through the final seconds
      g.sprite.alpha = g.ttl > 0 && g.ttl < blink
        ? 0.25 + 0.75 * Math.abs(Math.sin(g.animT * 9))
        : 1;
    }
  }

  /** Streak behind a magnet-pulled gem, growing with its speed. */
  private renderTrail(g: Gem): void {
    const speed = Math.hypot(g.vx, g.vy);
    const t = g.pulled
      ? LOOK.trailMin + (1 - LOOK.trailMin) * Math.min(1, speed / LOOK.trailFull)
      : 0;
    g.trail.visible = t > 0;
    if (t <= 0) return;
    g.trail.position.set(g.x, g.y);
    g.trail.rotation = Math.atan2(g.vy, g.vx);
    g.trail.tint = TRAIL_TINT[g.kind];
    g.trail.scale.set(LOOK.trailStretch * t, g.sprite.scale.y);
    g.trail.alpha = LOOK.trailAlpha * t * g.sprite.alpha;
  }

  clear(): void {
    while (this.live.length > 0) this.kill(this.live.length - 1);
  }
}
